import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdSearch, MdError, MdCheckCircle, MdPending, MdCancel } from "react-icons/md";
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ViewAnalyzed = () => {
  const navigate = useNavigate();
  const [analyzedData, setAnalyzedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://usibras-web-backend.onrender.com/api/fetchAnalyseRCN");
        
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const data = await response.json();
        setAnalyzedData(data.data);
      } catch (err) {
        setError(err.message);
        toast.error('Failed to fetch analyzed data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCalibrate = (id, type) => {
    navigate(`/admin/warehouse/calibrate-analyzed`, {
      state: { id, type }
    });
  };

  // Filter and search functionality
  const filteredData = analyzedData.filter(item => {
    const matchesSearch = 
      item.id.toString().includes(searchQuery) ||
      item.truck_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.origin.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = 
      filterType === "all" || 
      (filterType === "fairtrade" && item.rcn_type === "Fair Trade RCN") ||
      (filterType === "organic" && item.rcn_type === "Organic RCN") ||
      (filterType === "conventional" && item.rcn_type === "Conventional RCN");
    
    return matchesSearch && matchesType;
  });

  // Get status color and icon
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "pending calibration":
        return {
          color: "text-yellow-500",
          bg: "bg-yellow-50",
          icon: <MdPending className="h-5 w-5" />
        };
      case "calibrated":
        return {
          color: "text-green-500",
          bg: "bg-green-50",
          icon: <MdCheckCircle className="h-5 w-5" />
        };
      case "rejected":
        return {
          color: "text-red-500",
          bg: "bg-red-50",
          icon: <MdCancel className="h-5 w-5" />
        };
      default:
        return {
          color: "text-gray-500",
          bg: "bg-gray-50",
          icon: <MdPending className="h-5 w-5" />
        };
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-6"}>
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header and Search */}
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                  Analyzed Data
                </h4>
                <span className="rounded-full bg-brand-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm">
                  {filteredData.length} records
                </span>
              </div>
              <div className="flex w-full flex-col gap-4 md:w-[420px] md:flex-row">
                <div className="flex w-full items-center rounded-xl border bg-white px-4 py-2.5 shadow-sm transition-all duration-200 hover:shadow-md dark:bg-navy-700">
                  <MdSearch className="h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by ID, truck number, or region..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none placeholder:text-gray-400 dark:text-white"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="rounded-xl border bg-white px-4 py-2.5 shadow-sm transition-all duration-200 hover:shadow-md dark:bg-navy-700"
                >
                  <option value="all">All Types</option>
                  <option value="fairtrade">Fair Trade</option>
                  <option value="organic">Organic</option>
                  <option value="conventional">Conventional</option>
                </select>
              </div>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredData.map((item) => {
                const statusStyle = getStatusStyle(item.status);
                return (
                  <Card
                    key={item.id}
                    extra="p-5 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-navy-700"
                  >
                    <div className="flex flex-col gap-5">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <span className="rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-500">
                          {item.rcn_type || "Unknown Type"}
                        </span>
                        <div className={`flex items-center gap-2 rounded-full ${statusStyle.bg} px-4 py-1.5`}>
                          {statusStyle.icon}
                          <span className={`text-sm font-medium ${statusStyle.color}`}>
                            {item.status}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">ID</span>
                          <span className="font-semibold text-navy-700 dark:text-white">
                            {item.id}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Date</span>
                          <span className="font-semibold text-navy-700 dark:text-white">
                            {new Date(item.date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Truck Number</span>
                          <span className="font-semibold text-navy-700 dark:text-white">
                            {item.truck_number}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Origin</span>
                          <span className="font-semibold text-navy-700 dark:text-white">
                            {item.origin}
                          </span>
                        </div>
                      </div>

                      {/* Calibrate Button */}
                      <button
                        onClick={() => handleCalibrate(item.id, item.rcn_type)}
                        className="mt-2 w-full rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
                      >
                        Calibrate
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* No Results Message */}
            {filteredData.length === 0 && (
              <div className="mt-6 flex h-[200px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
                <p className="text-center text-gray-500 dark:text-gray-400">
                  No records found matching your search
                </p>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ViewAnalyzed; 